import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import {
  LayoutDashboard, Users, FolderOpen, Repeat, FileText, Package,
  TrendingDown, TrendingUp, BarChart2, Settings, LogOut, Zap, Bell, Menu, X, Sun, Moon, ClipboardList, Target,
  ChevronDown, FolderKanban, AlertTriangle, DollarSign, Wallet
} from 'lucide-react';
import { getInvoices, getExpiringSubs, getMyNotifications, markNotificationRead, markAllNotificationsRead, getSidebarStats } from '../../utils/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'الرئيسية', exact: true },
  
  {
    group: true,
    id: 'production',
    label: 'نظام التشغيل والإنتاج',
    icon: FolderOpen,
    children: [
      { to: '/clients', icon: Users, label: 'العملاء' },
      { to: '/projects', icon: FolderOpen, label: 'المشاريع' },
      { to: '/subscriptions', icon: Repeat, label: 'الاشتراكات' },
      { to: '/deliverables', icon: Zap, label: 'التسليمات' },
      { to: '/packages', icon: Package, label: 'إدارة الباقات' },
    ]
  },

  {
    group: true,
    id: 'accounting',
    label: 'نظام المحاسبة المالية',
    icon: Wallet,
    children: [
      { to: '/treasury', icon: DollarSign, label: 'الخزينة والديون' },
      { to: '/invoices', icon: FileText, label: 'الفواتير', hasBadge: 'unpaid_invoices_count' },
      { to: '/expenses', icon: TrendingDown, label: 'المصاريف' },
      { to: '/commissions', icon: TrendingUp, label: 'العمولات' },
    ]
  },

  {
    group: true,
    id: 'tasks_ops',
    label: 'العمليات والمهام 📋',
    icon: ClipboardList,
    children: [
      { to: '/tm/tasks', icon: ClipboardList, label: 'لوحة المهام', hasBadge: 'active_tasks_count' },
      { to: '/tm/teams', icon: FolderKanban, label: 'هيكلية الفرق' },
      { to: '/tm/dashboard', icon: BarChart2, label: 'أداء الفرق' },
    ]
  },

  {
    group: true,
    id: 'hr_staff',
    label: 'الموظفون والمالية 👥',
    icon: Users,
    children: [
      { to: '/employees', icon: Users, label: 'سجل الموظفين' },
      { to: '/tm/earnings', icon: DollarSign, label: 'المستحقات والأرباح' },
      { to: '/tm/supervisors', icon: Target, label: 'المتابعين', ownerOnly: true },
      { to: '/tm/complaints', icon: AlertTriangle, label: 'الشكاوى', ownerOnly: true, hasBadge: 'open_complaints_count' },
      { to: '/team', icon: Users, label: 'أداء الفريق (الداخلي)' },
    ]
  },

  {
    group: true,
    id: 'system',
    label: 'إدارة النظام',
    icon: Settings,
    children: [
      { to: '/reports', icon: BarChart2, label: 'التقارير' },
      { to: '/settings', icon: Settings, label: 'الإعدادات' },
    ]
  }
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [personalNotifs, setPersonalNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBell, setShowBell] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarStats, setSidebarStats] = useState({ unpaid_invoices_count: 0, active_tasks_count: 0, open_complaints_count: 0 });
  const [openGroups, setOpenGroups] = useState({
    production: location.pathname.match(/^\/(projects|deliverables|subscriptions|packages|clients)/) !== null,
    accounting: location.pathname.match(/^\/(invoices|treasury|expenses|commissions)/) !== null,
    tasks_ops: location.pathname.match(/^\/tm\/(tasks|teams|dashboard)/) !== null,
    hr_staff: location.pathname.match(/^\/(employees|team)/) !== null || location.pathname.match(/^\/tm\/(earnings|supervisors|complaints)/) !== null,
    system: location.pathname.match(/^\/(reports|settings)/) !== null
  });
  const bellRef = useRef(null);

  const handleLogout = () => { logout(); navigate('/login'); };

  const loadSidebarStats = async () => {
    try {
      const stats = await getSidebarStats();
      setSidebarStats(stats);
    } catch (e) {
      console.error('[sidebar] Failed to fetch stats:', e);
    }
  };

  const loadAlerts = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    Promise.all([
      getInvoices({ status: 'unpaid' }).catch(() => []),
      getInvoices({ status: 'partial' }).catch(() => []),
      getExpiringSubs().catch(() => []),
    ]).then(([unpaid, partial, expiring]) => {
      const list = [];
      const overdue = [...unpaid, ...partial].filter(i => i.due_date && i.due_date < todayStr);
      overdue.forEach(i => list.push({ type: 'overdue', msg: 'فاتورة متأخرة — ' + (i.client_name || i.invoice_number), id: 'inv-' + i.id }));
      expiring.forEach(s => list.push({ type: 'expiring', msg: 'اشتراك ينتهي قريباً — ' + (s.client_name || '') + ' (' + s.period_end + ')', id: 'sub-' + s.id }));
      setAlerts(list);
    });
  };

  const loadPersonalNotifs = async () => {
    try {
      const data = await getMyNotifications();
      setPersonalNotifs(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) { /* silently ignore */ }
  };

  useEffect(() => {
    loadAlerts();
    loadPersonalNotifs();
    loadSidebarStats();
    const timer = setInterval(() => { loadAlerts(); loadPersonalNotifs(); loadSidebarStats(); }, 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showBell) return;
    const handler = e => { if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showBell]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isEmployee = user?.role === 'employee';
  const isOwnerOrAdmin = ['owner', 'admin'].includes(user?.role);
  const userPermissions = user?.permissions || ['/tasks'];
  const visibleNavItems = navItems.filter(i => {
    if (i.divider) return true;
    if (i.group) return true;
    if (!isEmployee) return true;
    return userPermissions.includes(i.to);
  });

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-white/5 shrink-0 bg-white/[0.02]">
        <div className="flex items-center gap-3 text-right" dir="rtl">
          <img src="/logos/ms-media-icon.svg" alt="MS MEDIA" className="w-10 h-10 drop-shadow-[0_0_10px_rgba(155,142,196,0.3)] shrink-0" />
          <div>
            <div className="text-base font-black leading-tight tracking-widest">
              <span className="text-white">MS </span>
              <span className="text-primary font-black">MEDIA</span>
            </div>
            <div className="text-gray-400 text-xs mt-0.5">نظام المحاسبة المالي</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto custom-scrollbar">
        <div className="px-3 space-y-1">
          {visibleNavItems.map((item, idx) => {
            if (item.divider) {
              return (
                <div key={'div-' + idx} className="pt-4 pb-2 px-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider whitespace-nowrap">{item.label}</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                </div>
              );
            }
            // ── Collapsible Group ──
            if (item.group) {
              const GroupIcon = item.icon;
              const isGroupActive = item.children.some(c => location.pathname.startsWith(c.to));
              const visibleChildren = item.children.filter(c => !c.ownerOnly || isOwnerOrAdmin);
              const isOpen = openGroups[item.id];
              // Calculate group badge counts combined when collapsed
              const groupBadgeCount = item.children.reduce((sum, child) => {
                if (child.hasBadge && sidebarStats[child.hasBadge]) {
                  return sum + (sidebarStats[child.hasBadge] || 0);
                }
                return sum;
              }, 0);

              return (
                <div key={'grp-' + item.id} className="mt-1">
                  <button
                    onClick={() => setOpenGroups(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                      isGroupActive
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                    }`}
                  >
                    <GroupIcon size={18} className="shrink-0" />
                    <span className="flex-1 text-right">{item.label}</span>
                    {groupBadgeCount > 0 && !isOpen && (
                      <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 ml-1 block animate-pulse" />
                    )}
                    <ChevronDown size={14} className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="mr-5 pr-2 space-y-0.5 py-1 relative">
                      {/* Vertical line indicator */}
                      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-primary/30 via-white/10 to-transparent" />
                      {visibleChildren.map(child => {
                        const ChildIcon = child.icon;
                        const badgeValue = child.hasBadge ? sidebarStats[child.hasBadge] : 0;
                        const isChildActive = location.pathname === child.to;
                        return (
                          <NavLink key={child.to} to={child.to} className={({ isActive }) => `
                            flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs transition-all duration-200 relative
                            ${isActive
                              ? 'bg-gradient-to-r from-primary/15 to-blue-500/5 text-white font-semibold shadow-inner'
                              : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                            }
                          `}>
                            {/* Horizontal connection dot */}
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 absolute -right-[11.5px] transition-all duration-300 ${
                              isChildActive ? 'bg-primary scale-125 shadow-[0_0_8px_var(--primary)]' : 'bg-white/20'
                            }`} />
                            <ChildIcon size={14} className="shrink-0" />
                            <span className="flex-1 text-right">{child.label}</span>
                            {badgeValue > 0 && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                child.hasBadge === 'open_complaints_count' 
                                  ? 'bg-red-500 text-white animate-pulse' 
                                  : child.hasBadge === 'active_tasks_count'
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-emerald-500 text-white'
                              }`}>
                                {badgeValue}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
            const { to, icon: Icon, label, exact, hasBadge } = item;
            const badgeValue = hasBadge ? sidebarStats[hasBadge] : 0;
            return (
              <NavLink key={to} to={to} end={exact} className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 border-r-4
                ${isActive
                  ? 'bg-gradient-to-r from-primary/15 to-blue-500/5 text-white font-semibold border-primary shadow-inner'
                  : 'text-gray-400 hover:bg-white/5 hover:text-gray-200 border-transparent'
                }
              `}>
                <Icon size={18} className="shrink-0" />
                <span className="flex-1 text-right">{label}</span>
                {badgeValue > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    hasBadge === 'unpaid_invoices_count' 
                      ? 'bg-red-500 text-white shadow-sm shadow-red-500/25' 
                      : 'bg-primary text-white'
                  }`}>
                    {badgeValue}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* User */}
      <div className="p-4 border-t border-white/5 shrink-0 bg-white/[0.01]">
        <div className="flex items-center gap-3 mb-4 bg-white/5 p-3 rounded-xl border border-white/5 relative group transition-all duration-300 hover:bg-white/[0.08]" dir="rtl">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-dark to-primary flex items-center justify-center text-white font-bold text-sm shrink-0 uppercase shadow-md relative">
            {user?.name?.[0] || 'M'}
            {/* Live session active dot */}
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-slate-900 animate-pulse" />
          </div>
          <div className="overflow-hidden text-right">
            <div className="text-white text-sm font-semibold truncate group-hover:text-primary transition-colors duration-200">
              {user?.name || user?.username}
            </div>
            <div className="text-gray-400 text-xs">
              {user?.role === 'owner' ? 'المالك' : user?.role === 'finance' ? 'مالية' : 'عمليات'}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-gray-400 text-sm font-medium hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
        >
          <LogOut size={16} />
          تسجيل الخروج
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-deeper text-dark dark:text-gray-100 overflow-hidden selection:bg-primary/20 transition-colors duration-200">
      {/* ── Mobile Sidebar Overlay ── */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`
        fixed inset-y-0 right-0 z-50 w-72 bg-gradient-to-b from-[#0F1E33] via-[#091220] to-[#040810] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out border-l border-white/5
        lg:translate-x-0 lg:static lg:shrink-0
        ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <SidebarContent />
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative">
        {/* Topbar */}
        <header className="h-16 bg-white/80 dark:bg-dark-deeper/80 backdrop-blur-md border-b border-gray-200 dark:border-white/10 flex items-center justify-between px-4 lg:px-8 shrink-0 z-30 sticky top-0 transition-colors duration-200">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 -mr-2 text-gray-500 hover:text-dark dark:hover:text-gray-100 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
            <div className="text-dark dark:text-white font-bold text-lg hidden sm:block">
              <span className="font-black tracking-wider">MS </span><span className="text-primary-dark font-black tracking-wider">MEDIA</span>
              <span className="text-gray-400 font-normal mx-2">|</span>نظام المحاسبة
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="w-10 h-10 rounded-xl border border-gray-200 dark:border-white/10 dark:bg-white/5 bg-white dark:bg-dark-deeper flex items-center justify-center text-gray-500 dark:text-gray-300 transition-all duration-200 hover:shadow-md"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div ref={bellRef} className="relative">
              <button
                onClick={() => setShowBell(v => !v)}
                className={`w-10 h-10 rounded-xl border flex items-center justify-center relative transition-all duration-200 hover:shadow-md ${showBell ? 'bg-gray-50 border-gray-300 dark:bg-white/10 dark:border-white/20' : 'bg-white border-gray-200 dark:bg-white/5 dark:border-white/10'}`}
              >
                <Bell size={18} className={(alerts.length > 0 || unreadCount > 0) ? 'text-danger' : 'text-gray-500'} />
                {(alerts.length > 0 || unreadCount > 0) && (
                  <span className="absolute -top-1 -left-1 min-w-[18px] h-[18px] rounded-full bg-danger ring-2 ring-white dark:ring-dark-deeper text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse">
                    {alerts.length + unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showBell && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-80 bg-white dark:bg-dark-deeper rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100/50 dark:border-white/10 z-50 overflow-hidden transform origin-top-left transition-all">
                  <div className="px-5 py-4 border-b border-gray-50 dark:border-white/5 flex items-center justify-between bg-gray-50/50 dark:bg-white/5">
                    <span className="font-bold text-dark dark:text-white flex items-center gap-2">
                      التنبيهات
                      {(alerts.length + unreadCount) > 0 && (
                        <span className="bg-danger/10 text-danger text-xs px-2 py-0.5 rounded-full">{alerts.length + unreadCount}</span>
                      )}
                    </span>
                    {unreadCount > 0 && (
                      <button onClick={async () => { await markAllNotificationsRead(); loadPersonalNotifs(); }} className="text-xs text-primary hover:underline">قراءة الكل</button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto custom-scrollbar">
                    {/* Personal notifications first */}
                    {personalNotifs.filter(n => !n.is_read).map(n => (
                      <div key={'pn-' + n.id} className="p-4 border-b border-gray-50 dark:border-white/5 hover:bg-blue-50/50 dark:hover:bg-white/10 transition-colors flex items-start gap-3 group cursor-pointer bg-blue-50/30 dark:bg-blue-900/10" onClick={async () => { await markNotificationRead(n.id); loadPersonalNotifs(); if (n.link) { setShowBell(false); navigate(n.link); } }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-600">
                          📋
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-dark dark:text-gray-100">{n.title}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{n.message}</div>
                          <div className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString('ar-IQ')}</div>
                        </div>
                      </div>
                    ))}

                    {/* System alerts */}
                    {alerts.map(a => (
                      <div key={a.id} className="p-4 border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/80 dark:hover:bg-white/10 transition-colors flex items-start gap-3 group cursor-pointer" onClick={() => navigate('/invoices')}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${a.type === 'overdue' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>
                          {a.type === 'overdue' ? '⚠️' : '⏰'}
                        </div>
                        <div className="flex-1 mt-1">
                          <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug group-hover:text-dark dark:group-hover:text-white transition-colors">{a.msg}</span>
                        </div>
                      </div>
                    ))}

                    {alerts.length === 0 && personalNotifs.filter(n => !n.is_read).length === 0 && (
                      <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-white/5 flex items-center justify-center text-2xl mb-2">✅</div>
                        <span className="text-sm">لا توجد تنبيهات جديدة</span>
                      </div>
                    )}
                  </div>

                  {(alerts.length > 0 || personalNotifs.length > 0) && (
                    <div className="p-3 border-t border-gray-50 dark:border-white/5 text-center bg-gray-50/50 dark:bg-white/5">
                      <button onClick={() => { setShowBell(false); navigate('/invoices'); }} className="text-primary dark:text-accent text-sm font-semibold hover:text-primary/80 transition-colors">
                        عرض الإجراءات المطلوبة ←
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Dynamic Page Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8 custom-scrollbar relative z-10 w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
